/**
 * Surface LIC (Line Integral Convolution) Renderer
 *
 * Implements GPU-based LIC visualization for surface flow patterns.
 * Uses custom GLSL shaders to perform LIC convolution in real-time.
 *
 * LIC works by:
 * 1. Generating a noise texture
 * 2. For each pixel, tracing a streamline forward and backward
 * 3. Averaging noise values along the streamline
 * 4. This creates coherent patterns aligned with the flow direction
 */

class SurfaceLICRenderer {
    constructor(scene, colormap = 'jet') {
        this.scene = scene;
        this.colormap = colormap;
        this.mesh = null;
        this.noiseTexture = null;
        this.colormapTexture = null;
        this.material = null;
        this.animationSpeed = 0;
        this.animationPhase = 0;
        this.animationId = null;

        // LIC parameters
        this.kernelLength = 32;
        this.contrast = 1.5;
        this.colorMode = 'magnitude';
        this.opacity = 1.0;
        this.showLIC = true;
        this.animateLIC = false;

        console.log('[SurfaceLIC] Renderer initialized');
    }

    /**
     * Create LIC visualization from server data
     * @param {Object} data - Data from SurfaceLICFilter
     * @param {Object} options - Visualization options
     * @returns {THREE.Mesh} - The created mesh
     */
    createLIC(data, options = {}) {
        console.log('[SurfaceLIC] Creating LIC visualization...');

        // Parse options
        this.kernelLength = options.kernelLength || data.kernel_length || 32;
        this.contrast = options.contrast || data.contrast || 1.5;
        this.colorMode = options.colorMode || data.color_mode || 'magnitude';
        this.opacity = options.opacity || 1.0;

        // Remove existing mesh
        if (this.mesh) {
            this.scene.remove(this.mesh);
            if (this.mesh.geometry) this.mesh.geometry.dispose();
            if (this.mesh.material) this.mesh.material.dispose();
        }

        // Create geometry
        const geometry = this._createGeometry(data);

        // Load noise texture
        this._loadNoiseTexture(data.noise_texture);

        // Create colormap texture
        this._createColormapTexture();

        // Create shader material
        this.material = this._createShaderMaterial(data);

        // Create mesh
        this.mesh = new THREE.Mesh(geometry, this.material);
        this.mesh.name = 'SurfaceLIC';
        this.mesh.userData.type = 'lic';
        this.mesh.userData.magnitudeRange = data.magnitude_range;

        this.scene.add(this.mesh);

        console.log(`[SurfaceLIC] Created LIC with ${data.num_vertices} vertices, ${data.num_triangles} triangles`);

        // Start animation if enabled
        if (this.animateLIC && this.animationSpeed > 0) {
            this.startAnimation();
        }

        return this.mesh;
    }

    /**
     * Create Three.js geometry from data
     * @private
     */
    _createGeometry(data) {
        const geometry = new THREE.BufferGeometry();

        // Vertices
        const vertices = new Float32Array(data.vertices);
        geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

        // Normals
        const normals = new Float32Array(data.normals);
        geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));

        // UVs for noise texture lookup
        const uvs = new Float32Array(data.uvs);
        geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));

        // 3D Tangent vectors (velocity projected onto surface, world space)
        // We accept either uv_tangents (2D, old format) or tangent_vectors (3D, new format)
        if (data.tangent_vectors) {
            const tangents = new Float32Array(data.tangent_vectors);
            geometry.setAttribute('tangentVector', new THREE.BufferAttribute(tangents, 3));
        } else if (data.uv_tangents) {
            // Legacy 2D format - pad with zeros for Z
            const uvTangents = new Float32Array(data.uv_tangents);
            const tangents = new Float32Array(uvTangents.length / 2 * 3);
            for (let i = 0; i < uvTangents.length / 2; i++) {
                tangents[i * 3] = uvTangents[i * 2];
                tangents[i * 3 + 1] = uvTangents[i * 2 + 1];
                tangents[i * 3 + 2] = 0;
            }
            geometry.setAttribute('tangentVector', new THREE.BufferAttribute(tangents, 3));
        }

        // Magnitudes for coloring
        const magnitudes = new Float32Array(data.magnitudes);
        geometry.setAttribute('magnitude', new THREE.BufferAttribute(magnitudes, 1));

        // Indices
        const indices = new Uint32Array(data.indices);
        geometry.setIndex(new THREE.BufferAttribute(indices, 1));

        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        return geometry;
    }

    /**
     * Load noise texture from base64 data
     * @private
     */
    _loadNoiseTexture(noiseData) {
        if (this.noiseTexture) {
            this.noiseTexture.dispose();
        }

        const loader = new THREE.TextureLoader();
        this.noiseTexture = loader.load(noiseData, (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
            if (this.material) {
                this.material.uniforms.noiseTexture.value = texture;
                this.material.needsUpdate = true;
            }
        });

        this.noiseTexture.wrapS = THREE.RepeatWrapping;
        this.noiseTexture.wrapT = THREE.RepeatWrapping;
    }

    /**
     * Create colormap texture for magnitude coloring
     * @private
     */
    _createColormapTexture() {
        if (this.colormapTexture) {
            this.colormapTexture.dispose();
        }

        // Create a 256x1 texture with colormap
        const size = 256;
        const data = new Uint8Array(size * 4);

        for (let i = 0; i < size; i++) {
            const t = i / (size - 1);
            const color = this._sampleColormap(t);
            data[i * 4] = Math.floor(color.r * 255);
            data[i * 4 + 1] = Math.floor(color.g * 255);
            data[i * 4 + 2] = Math.floor(color.b * 255);
            data[i * 4 + 3] = 255;
        }

        this.colormapTexture = new THREE.DataTexture(data, size, 1, THREE.RGBAFormat);
        this.colormapTexture.needsUpdate = true;
        this.colormapTexture.minFilter = THREE.LinearFilter;
        this.colormapTexture.magFilter = THREE.LinearFilter;
    }

    /**
     * Parse hex color to RGB (0-1 range)
     * @private
     */
    _hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16) / 255,
            g: parseInt(result[2], 16) / 255,
            b: parseInt(result[3], 16) / 255
        } : { r: 0, g: 0, b: 0 };
    }

    /**
     * Interpolate between color stops
     * @private
     */
    _interpolateColors(colors, t) {
        if (colors.length === 0) return { r: 0, g: 0, b: 0 };
        if (colors.length === 1) return this._hexToRgb(colors[0]);

        // Clamp t to [0, 1]
        t = Math.max(0, Math.min(1, t));

        // Find the two color stops to interpolate between
        const numStops = colors.length;
        const scaledT = t * (numStops - 1);
        const lowerIndex = Math.floor(scaledT);
        const upperIndex = Math.min(lowerIndex + 1, numStops - 1);
        const localT = scaledT - lowerIndex;

        const c1 = this._hexToRgb(colors[lowerIndex]);
        const c2 = this._hexToRgb(colors[upperIndex]);

        return {
            r: c1.r + (c2.r - c1.r) * localT,
            g: c1.g + (c2.g - c1.g) * localT,
            b: c1.b + (c2.b - c1.b) * localT
        };
    }

    /**
     * Sample colormap at position t (0-1)
     * @private
     */
    _sampleColormap(t) {
        // Use centralized Colormap if available
        if (window.Colormap && window.Colormap.getColor) {
            // Get solid color for surface LIC dropdown if using solid colormap
            const solidColorHex = window.Colormap.isSolidColor(this.colormap)
                ? window.Colormap.getSolidColor('surfaceLICColormap')
                : null;
            const rgb = window.Colormap.getColor(t, this.colormap, solidColorHex);
            return {
                r: rgb[0] / 255,
                g: rgb[1] / 255,
                b: rgb[2] / 255
            };
        }

        // Fallback to local colormaps if Colormap not available
        const colormaps = {
            jet: ['#00007f', '#0000ff', '#00ffff', '#ffff00', '#ff0000', '#7f0000'],
            viridis: ['#440154', '#482878', '#3e4a89', '#31688e', '#26838f', '#1f9e89', '#35b779', '#6ece58', '#b5de2b', '#fde724'],
            coolwarm: ['#3b4cc0', '#6688ee', '#88aaff', '#aaccff', '#dddddd', '#ffccaa', '#ff9966', '#ee6644', '#b40426'],
            plasma: ['#0d0887', '#5c01a6', '#9c179e', '#cc4778', '#ed7953', '#fdb42f', '#f0f921'],
            rainbow: ['#ff0000', '#ff7f00', '#ffff00', '#00ff00', '#0000ff', '#4b0082', '#9400d3'],
            turbo: ['#30123b', '#4454c4', '#4777ef', '#1ac9c6', '#56e978', '#a0da39', '#e1c034', '#fca50a', '#e45503', '#d02c2c'],
            grayscale: ['#000000', '#ffffff']
        };

        const colors = colormaps[this.colormap] || colormaps.jet;
        return this._interpolateColors(colors, t);
    }

    /**
     * Create GLSL shader material for LIC
     * @private
     */
    _createShaderMaterial(data) {
        const magnitudeRange = data.magnitude_range;

        const vertexShader = `
            attribute vec3 tangentVector;  // 3D tangent vector (velocity projected onto surface)
            attribute float magnitude;

            varying vec2 vScreenCoord;     // Screen coordinates for noise lookup
            varying vec3 vNormal;          // View-space normal
            varying vec3 vTangentView;     // View-space tangent vector
            varying float vMagnitude;
            varying vec3 vPosition;

            void main() {
                // Transform normal and tangent to view space
                vNormal = normalize(normalMatrix * normal);
                vTangentView = normalMatrix * tangentVector;

                vMagnitude = magnitude;
                vPosition = position;

                vec4 clipPos = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                gl_Position = clipPos;

                // Screen coordinates will be computed in fragment shader from gl_FragCoord
                vScreenCoord = vec2(0.0); // Placeholder, actual computation in fragment shader
            }
        `;

        const fragmentShader = `
            precision highp float;

            uniform sampler2D noiseTexture;
            uniform sampler2D colormapTexture;
            uniform float kernelLength;
            uniform float contrast;
            uniform float opacity;
            uniform float magnitudeMin;
            uniform float magnitudeMax;
            uniform float animationPhase;
            uniform int colorMode; // 0: magnitude, 1: direction, 2: monochrome
            uniform bool showLIC;
            uniform vec2 resolution;  // Screen resolution for coordinate normalization

            varying vec3 vNormal;
            varying vec3 vTangentView;
            varying float vMagnitude;
            varying vec3 vPosition;

            // LIC convolution in screen space
            float computeLIC(vec2 screenTC, vec2 flowDir, float stepSize) {
                float sum = 0.0;
                float weight = 0.0;

                // Normalize flow direction
                float flowLen = length(flowDir);
                vec2 dir = flowLen > 0.001 ? flowDir / flowLen : vec2(0.0);

                // Subtract animation offset to make pattern flow WITH velocity
                vec2 animOffset = dir * animationPhase * 0.05;

                // Forward integration
                vec2 pos = screenTC - animOffset;
                for (int i = 0; i < 64; i++) {
                    if (float(i) >= kernelLength * 0.5) break;

                    float noise = texture2D(noiseTexture, fract(pos)).r;
                    float w = 1.0 - float(i) / (kernelLength * 0.5);
                    sum += noise * w;
                    weight += w;

                    pos += dir * stepSize;
                }

                // Backward integration
                pos = screenTC - animOffset;
                for (int i = 0; i < 64; i++) {
                    if (float(i) >= kernelLength * 0.5) break;

                    float noise = texture2D(noiseTexture, fract(pos)).r;
                    float w = 1.0 - float(i) / (kernelLength * 0.5);
                    sum += noise * w;
                    weight += w;

                    pos -= dir * stepSize;
                }

                return weight > 0.0 ? sum / weight : 0.5;
            }

            void main() {
                // Project tangent vector onto surface plane (remove normal component)
                // This is the ParaView approach: tcoordLIC = tcoordLIC - k*normN
                vec3 normN = normalize(vNormal);
                float k = dot(vTangentView, normN);
                vec3 projectedTangent = vTangentView - k * normN;

                // Use X,Y components as the flow direction in screen space
                vec2 flowDir = projectedTangent.xy;

                // Compute screen-space texture coordinates
                // Scale to tile the noise texture across the screen
                vec2 screenTC = gl_FragCoord.xy / resolution * 4.0;  // Tile 4x across screen

                // Scale flow direction to screen space (normalize by resolution)
                vec2 screenFlowDir = flowDir / resolution * 200.0;  // Scale factor for visibility

                // Compute LIC value
                float stepSize = 0.003;
                float lic = showLIC ? computeLIC(screenTC, screenFlowDir, stepSize) : 0.5;

                // Apply contrast enhancement
                lic = clamp((lic - 0.5) * contrast + 0.5, 0.0, 1.0);

                // Normalize magnitude for color lookup
                float normalizedMag = (vMagnitude - magnitudeMin) / max(magnitudeMax - magnitudeMin, 0.0001);
                normalizedMag = clamp(normalizedMag, 0.0, 1.0);

                // Get color from colormap
                vec3 baseColor;
                if (colorMode == 0) {
                    // Color by magnitude
                    baseColor = texture2D(colormapTexture, vec2(normalizedMag, 0.5)).rgb;
                } else if (colorMode == 1) {
                    // Color by direction (encode flow direction as color)
                    vec2 normDir = length(flowDir) > 0.001 ? normalize(flowDir) * 0.5 + 0.5 : vec2(0.5);
                    baseColor = vec3(normDir.x, normDir.y, 0.5);
                } else {
                    // Monochrome
                    baseColor = vec3(1.0);
                }

                // Combine LIC pattern with color
                vec3 finalColor = baseColor * (0.3 + 0.7 * lic);

                // Add lighting based on normal
                vec3 lightDir = normalize(vec3(1.0, 1.0, 1.0));
                float diffuse = max(0.3, dot(normN, lightDir));
                finalColor *= diffuse;

                gl_FragColor = vec4(finalColor, opacity);
            }
        `;

        // Get initial resolution (will be updated on render)
        const initialResolution = new THREE.Vector2(window.innerWidth, window.innerHeight);

        return new THREE.ShaderMaterial({
            uniforms: {
                noiseTexture: { value: this.noiseTexture },
                colormapTexture: { value: this.colormapTexture },
                kernelLength: { value: this.kernelLength },
                contrast: { value: this.contrast },
                opacity: { value: this.opacity },
                magnitudeMin: { value: magnitudeRange[0] },
                magnitudeMax: { value: magnitudeRange[1] },
                animationPhase: { value: 0.0 },
                colorMode: { value: this.colorMode === 'magnitude' ? 0 : (this.colorMode === 'direction' ? 1 : 2) },
                showLIC: { value: this.showLIC },
                resolution: { value: initialResolution }
            },
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            side: THREE.DoubleSide,
            transparent: this.opacity < 1.0
        });
    }

    /**
     * Update resolution uniform (call when canvas resizes)
     * @param {number} width - Canvas width
     * @param {number} height - Canvas height
     */
    updateResolution(width, height) {
        if (this.material && this.material.uniforms.resolution) {
            this.material.uniforms.resolution.value.set(width, height);
        }
    }

    /**
     * Update LIC parameters
     * @param {Object} params - New parameters
     */
    updateParameters(params) {
        if (!this.material) return;

        if (params.kernelLength !== undefined) {
            this.kernelLength = params.kernelLength;
            this.material.uniforms.kernelLength.value = params.kernelLength;
        }

        if (params.contrast !== undefined) {
            this.contrast = params.contrast;
            this.material.uniforms.contrast.value = params.contrast;
        }

        if (params.opacity !== undefined) {
            this.opacity = params.opacity;
            this.material.uniforms.opacity.value = params.opacity;
            this.material.transparent = params.opacity < 1.0;
        }

        if (params.colorMode !== undefined) {
            this.colorMode = params.colorMode;
            this.material.uniforms.colorMode.value =
                params.colorMode === 'magnitude' ? 0 :
                (params.colorMode === 'direction' ? 1 : 2);
        }

        if (params.showLIC !== undefined) {
            this.showLIC = params.showLIC;
            this.material.uniforms.showLIC.value = params.showLIC;
        }

        if (params.colormap !== undefined) {
            this.colormap = params.colormap;
            this._createColormapTexture();
            this.material.uniforms.colormapTexture.value = this.colormapTexture;
        }

        this.material.needsUpdate = true;
    }

    /**
     * Start LIC animation
     */
    startAnimation(speed = 1.0) {
        this.animateLIC = true;
        this.animationSpeed = speed;

        const animate = () => {
            if (!this.animateLIC) return;

            this.animationPhase += this.animationSpeed * 0.016; // Assuming 60fps

            if (this.material) {
                this.material.uniforms.animationPhase.value = this.animationPhase;
            }

            this.animationId = requestAnimationFrame(animate);
        };

        animate();
        console.log('[SurfaceLIC] Animation started');
    }

    /**
     * Stop LIC animation
     */
    stopAnimation() {
        this.animateLIC = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        console.log('[SurfaceLIC] Animation stopped');
    }

    /**
     * Set animation speed
     * @param {number} speed - Animation speed multiplier
     */
    setAnimationSpeed(speed) {
        this.animationSpeed = speed;
    }

    /**
     * Toggle visibility
     * @param {boolean} visible
     */
    setVisible(visible) {
        if (this.mesh) {
            this.mesh.visible = visible;
        }
    }

    /**
     * Clean up resources
     */
    dispose() {
        this.stopAnimation();

        if (this.mesh) {
            this.scene.remove(this.mesh);
            if (this.mesh.geometry) this.mesh.geometry.dispose();
        }

        if (this.material) {
            this.material.dispose();
        }

        if (this.noiseTexture) {
            this.noiseTexture.dispose();
        }

        if (this.colormapTexture) {
            this.colormapTexture.dispose();
        }

        this.mesh = null;
        this.material = null;
        this.noiseTexture = null;
        this.colormapTexture = null;

        console.log('[SurfaceLIC] Disposed');
    }
}

// Export for use in viewer
if (typeof window !== 'undefined') {
    window.SurfaceLICRenderer = SurfaceLICRenderer;
}
